/**
 * The poster studio's embed protocol, as euler speaks it. The contract lives in
 * the posters repo (`docs/embed-protocol.md`, `libs/schema/src/embed.ts`); this
 * is the host's copy of the parts it uses.
 */

export const kPosterStudioProtocolVersion = 2;

export type PosterImageFormat = 'image/jpeg' | 'image/png';

/** What the host can do with a poster's pictures. */
export interface PosterStudioCapabilities {
  share: boolean;
  save: boolean;
  /** The studio's top screens show ‹, which asks the host to leave. */
  exit: boolean;
}

/** Host → studio: open, signed in with the one-time token. */
export interface PosterStudioInitEnvelope {
  v: typeof kPosterStudioProtocolVersion;
  type: 'init';
  payload: {
    token: string;
    orgId: number;
    storeId: number;
    locale: 'zh-CN';
    theme: 'light' | 'dark';
    capabilities: PosterStudioCapabilities;
  };
}

/** Host → studio: may it close? Answered `close`, or `close-refused`. */
export interface PosterStudioCloseRequestEnvelope {
  v: typeof kPosterStudioProtocolVersion;
  type: 'close-request';
  payload: Record<string, never>;
  requestId: string;
}

export type PosterStudioHostEnvelope =
  | PosterStudioInitEnvelope
  | PosterStudioCloseRequestEnvelope;

/** A picture as an iframe hands it over: the bytes, by structured clone. */
export interface PosterBytesImage {
  kind: 'bytes';
  bytes: Uint8Array;
  format: PosterImageFormat;
}
/** A picture as a native WebView hands it over: base64 in JSON. */
export interface PosterBase64Image {
  kind: 'base64';
  base64: string;
  format: PosterImageFormat;
}
export type PosterImage = PosterBytesImage | PosterBase64Image;

export interface PosterStudioReady {
  kind: 'ready';
}
export interface PosterStudioShare {
  kind: 'share';
  caption: string;
  images: PosterImage[];
}
export interface PosterStudioSave {
  kind: 'save';
  images: PosterImage[];
}
export interface PosterStudioClosed {
  kind: 'closed';
  requestId: string | null;
}
export interface PosterStudioCloseRefused {
  kind: 'close-refused';
  requestId: string | null;
  message: string;
}
/** The shop pressed ‹ on the studio's top screen. */
export interface PosterStudioExit {
  kind: 'exit';
}
export interface PosterStudioError {
  kind: 'error';
  code: string;
  message: string;
}
/** Studio → host, as euler acts on it. `design:saved` is not acted on. */
export type PosterStudioEvent =
  | PosterStudioReady
  | PosterStudioShare
  | PosterStudioSave
  | PosterStudioClosed
  | PosterStudioCloseRefused
  | PosterStudioExit
  | PosterStudioError;

/** The codes that mean the sign-in failed; a new token is the remedy. */
export const kPosterStudioSignInErrors = [
  'sign-in-rejected',
  'sign-in-unavailable',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFormat(value: unknown): value is PosterImageFormat {
  return value === 'image/jpeg' || value === 'image/png';
}

function decodeImage(value: unknown): PosterImage | null {
  if (!isRecord(value) || !isFormat(value.format)) return null;
  if (value.bytes instanceof Uint8Array)
    return { kind: 'bytes', bytes: value.bytes, format: value.format };
  if (typeof value.base64 === 'string' && value.base64 !== '')
    return { kind: 'base64', base64: value.base64, format: value.format };
  return null;
}

function decodeImages(value: unknown): PosterImage[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const images = value.map(decodeImage);
  return images.every((image): image is PosterImage => image !== null)
    ? images
    : null;
}

/**
 * The studio's message, or null for anything else - another library's
 * message, a version this host does not speak, a malformed payload.
 */
export function decodePosterStudioMessage(
  data: unknown,
): PosterStudioEvent | null {
  if (!isRecord(data) || data.v !== kPosterStudioProtocolVersion) return null;
  const payload = isRecord(data.payload) ? data.payload : null;
  if (payload === null) return null;
  const requestId = typeof data.requestId === 'string' ? data.requestId : null;
  switch (data.type) {
    case 'ready':
      return { kind: 'ready' };
    case 'share': {
      const images = decodeImages(payload.images);
      if (images === null || typeof payload.caption !== 'string') return null;
      return { kind: 'share', caption: payload.caption, images };
    }
    case 'save': {
      const images = decodeImages(payload.images);
      return images === null ? null : { kind: 'save', images };
    }
    case 'close':
      return { kind: 'closed', requestId };
    case 'exit':
      return { kind: 'exit' };
    case 'close-refused':
      return typeof payload.message === 'string'
        ? { kind: 'close-refused', requestId, message: payload.message }
        : null;
    case 'error':
      return typeof payload.code === 'string' &&
        typeof payload.message === 'string'
        ? { kind: 'error', code: payload.code, message: payload.message }
        : null;
    default:
      return null;
  }
}

/** A native WebView's message: a JSON string. */
export function decodeNativePosterStudioMessage(
  data: string,
): PosterStudioEvent | null {
  try {
    return decodePosterStudioMessage(JSON.parse(data));
  } catch (error) {
    console.error('poster studio: a message was not JSON:', error);
    return null;
  }
}

/** The token the backend signs for the studio (`PosterStudioTokenGrant`). */
export interface PosterStudioTokenGrant {
  token: string;
  /** Unix seconds. */
  expiresAt: number;
}
